import { useState, FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL } from '../../lib/format';
import { ArrowLeft, Save } from 'lucide-react';

interface Associado { id: string; nome: string; documentoMask: string; matriculaServidor: string; orgaoPublico: string | null; }

function proximaCompetencia(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function ContratoNovoPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const associadoIdFromUrl = params.get('associadoId') ?? '';

  const [form, setForm] = useState({
    associadoId: associadoIdFromUrl,
    origemOperacional: 'novo',
    mensalidadeAssociativa: '',
    valorAuxilio: '',
    qtdParcelas: '3',
    primeiraCompetencia: proximaCompetencia(),
    comissaoPercentual: '',
    observacoes: '',
  });
  const [error, setError] = useState<string | null>(null);

  // Busca o associado se vier pré-selecionado
  const { data: associado } = useQuery({
    queryKey: ['associado-select', form.associadoId],
    queryFn: () => api<Associado>(`/associados/${form.associadoId}`),
    enabled: !!form.associadoId && /^\d+$/.test(form.associadoId),
  });

  // Busca associados pra dropdown (se não veio pré-selecionado)
  const { data: associadosList } = useQuery({
    queryKey: ['associados-todos'],
    queryFn: () => api<{ items: Associado[]; total: number }>('/associados/?size=200'),
    enabled: !associadoIdFromUrl,
  });

  const create = useMutation({
    mutationFn: (data: any) => api<{ id: string }>('/contratos/', { method: 'POST', body: data }),
    onSuccess: (result) => navigate(`/contratos/${result.id}`),
    onError: (err: ApiException) => {
      const msg = err.apiError?.message ?? 'Falha ao criar contrato';
      const details = (err.apiError?.details as any)?.fieldErrors;
      if (details) {
        const m = Object.entries(details).map(([k, v]: any) => `${k}: ${v.join(', ')}`).join(' · ');
        setError(`${msg} (${m})`);
      } else setError(msg);
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const mensal = Number(form.mensalidadeAssociativa.replace(',', '.'));
    const qtd = parseInt(form.qtdParcelas, 10);
    if (!form.associadoId || !mensal || !qtd) {
      setError('Preencha associado, mensalidade e quantidade de parcelas.');
      return;
    }
    const payload: any = {
      associadoId: form.associadoId,
      origemOperacional: form.origemOperacional,
      mensalidadeAssociativa: mensal,
      qtdParcelas: qtd,
      primeiraCompetencia: form.primeiraCompetencia,
    };
    if (form.valorAuxilio) payload.valorAuxilio = Number(form.valorAuxilio.replace(',', '.'));
    if (form.comissaoPercentual) payload.comissaoPercentual = Number(form.comissaoPercentual.replace(',', '.'));
    if (form.observacoes) payload.observacoes = form.observacoes;
    create.mutate(payload);
  }

  const valorTotal = (() => {
    const mensal = Number(form.mensalidadeAssociativa.replace(',', '.'));
    const qtd = parseInt(form.qtdParcelas, 10);
    if (!mensal || !qtd) return 0;
    return mensal * qtd;
  })();

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to={associadoIdFromUrl ? `/associados/${associadoIdFromUrl}` : '/contratos'}
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="h-3 w-3" />voltar
      </Link>
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Novo contrato</h1>
        <p className="text-muted-foreground">Os dados gerarão as parcelas mensais automaticamente.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Associado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {associadoIdFromUrl && associado && (
              <div className="p-3 bg-muted/50 rounded-md">
                <div className="font-medium">{associado.nome}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {associado.documentoMask} · matrícula {associado.matriculaServidor}
                </div>
              </div>
            )}
            {!associadoIdFromUrl && (
              <div>
                <label className="text-sm font-medium" htmlFor="associadoId">Selecione</label>
                <select
                  id="associadoId"
                  value={form.associadoId}
                  onChange={(e) => setForm(f => ({ ...f, associadoId: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                  required
                >
                  <option value="">— escolha um associado —</option>
                  {associadosList?.items.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome} · {a.documentoMask}</option>
                  ))}
                </select>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-lg">Operação</CardTitle>
            <CardDescription>Mensalidade × quantidade = valor total do contrato</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="origem">Origem</label>
                <select id="origem" value={form.origemOperacional}
                  onChange={(e) => setForm(f => ({ ...f, origemOperacional: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="novo">Novo</option>
                  <option value="renovacao">Renovação</option>
                  <option value="refinanciamento">Refinanciamento</option>
                  <option value="portabilidade">Portabilidade</option>
                  <option value="migracao_etipi">Migração ETIPI</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="primeiraCompetencia">Primeira competência</label>
                <Input id="primeiraCompetencia" type="text" placeholder="2026-02"
                  value={form.primeiraCompetencia}
                  onChange={(e) => setForm(f => ({ ...f, primeiraCompetencia: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="mensalidade">Mensalidade associativa (R$) *</label>
                <Input id="mensalidade" type="text" placeholder="400,00"
                  value={form.mensalidadeAssociativa}
                  onChange={(e) => setForm(f => ({ ...f, mensalidadeAssociativa: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="valorAuxilio">Valor do auxílio emergencial (R$)</label>
                <Input id="valorAuxilio" type="text" placeholder="1050,00"
                  value={form.valorAuxilio}
                  onChange={(e) => setForm(f => ({ ...f, valorAuxilio: e.target.value }))} />
                <p className="text-xs text-muted-foreground mt-1">
                  Valor que o associado recebe na conta. Base da comissão do agente (10%).
                </p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="qtd">Quantidade de parcelas *</label>
                <Input id="qtd" type="number" min="1" max="24"
                  value={form.qtdParcelas}
                  onChange={(e) => setForm(f => ({ ...f, qtdParcelas: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="comissao">Comissão (%) — opcional</label>
                <Input id="comissao" type="text" placeholder="10"
                  value={form.comissaoPercentual}
                  onChange={(e) => setForm(f => ({ ...f, comissaoPercentual: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Valor total calculado</label>
                <div className="h-10 px-3 py-2 text-sm rounded-md bg-muted/50 border border-input flex items-center font-medium">
                  {fmtBRL(valorTotal)}
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="obs">Observações</label>
              <textarea id="obs" rows={3}
                value={form.observacoes}
                onChange={(e) => setForm(f => ({ ...f, observacoes: e.target.value }))}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mt-4 p-3 border border-destructive/30 bg-destructive/10 rounded-md text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <><Spinner className="mr-2 h-4 w-4" />criando…</> : <><Save className="h-4 w-4 mr-2" />Criar contrato</>}
          </Button>
          <Link to={associadoIdFromUrl ? `/associados/${associadoIdFromUrl}` : '/contratos'}>
            <Button type="button" variant="ghost">Cancelar</Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
